import React from 'react';
import { EyeIcon, PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, MapPinIcon, BuildingIcon, CheckCircleIcon } from 'lucide-react';
import { Location } from '../../types/location';
interface LocationListProps {
  locations: Location[];
  loading: boolean;
  sortConfig: {
    field: string;
    direction: 'asc' | 'desc';
  };
  onSort: (field: string) => void;
  selectedLocations: string[];
  onSelectionChange: (selected: string[]) => void;
}
const LocationList: React.FC<LocationListProps> = ({
  locations,
  loading,
  sortConfig,
  onSort,
  selectedLocations,
  onSelectionChange
}) => {
  const renderSortIcon = (field: string) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? <ChevronUpIcon size={14} className="ml-1" data-id="element-195" /> : <ChevronDownIcon size={14} className="ml-1" data-id="element-196" />;
  };
  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'Pickup':
        return <MapPinIcon size={16} className="text-green-600" data-id="element-197" />;
      case 'Delivery':
        return <BuildingIcon size={16} className="text-blue-600" data-id="element-198" />;
      default:
        return <CheckCircleIcon size={16} className="text-primary-600" data-id="element-199" />;
    }
  };
  if (loading) {
    return <div className="animate-pulse" data-id="element-200">
        <div className="h-10 bg-gray-200 rounded mb-4" data-id="element-201"></div>
        <div className="space-y-4" data-id="element-202">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-gray-200 rounded" data-id="element-203"></div>)}
        </div>
      </div>;
  }
  if (locations.length === 0) {
    return <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-id="element-204">
        <MapPinIcon size={40} className="mx-auto text-gray-400 mb-4" data-id="element-205" />
        <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-206">
          No locations found
        </h3>
        <p className="text-gray-500 mb-6" data-id="element-207">
          Get started by adding your first location or adjusting your search
          filters.
        </p>
        <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700" data-id="element-208">
          Add Your First Location
        </button>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200" data-id="element-209">
      {selectedLocations.length > 0 && <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-210">
          <div className="flex items-center justify-between" data-id="element-211">
            <span className="text-sm text-gray-700" data-id="element-212">
              {selectedLocations.length} location
              {selectedLocations.length === 1 ? '' : 's'} selected
            </span>
            <div className="space-x-2" data-id="element-213">
              <button className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-214">
                Export Selected
              </button>
              <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-215">
                Delete Selected
              </button>
            </div>
          </div>
        </div>}
      <div className="overflow-x-auto" data-id="element-216">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-217">
          <thead className="bg-gray-50" data-id="element-218">
            <tr data-id="element-219">
              <th scope="col" className="w-12 px-6 py-3" data-id="element-220">
                <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" checked={selectedLocations.length === locations.length && locations.length > 0} onChange={e => onSelectionChange(e.target.checked ? locations.map(loc => loc.id) : [])} data-id="element-221" />
              </th>
              {[['id', 'Location ID'], ['name', 'Location Name'], ['locationType', 'Type'], ['addressLine1', 'Address'], ['city', 'City'], ['state', 'State'], ['zip', 'ZIP'], ['lastUsedDate', 'Last Used']].map(([field, label]) => <th key={field} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => onSort(field)} data-id="element-222">
                  <div className="flex items-center" data-id="element-223">
                    {label}
                    {renderSortIcon(field)}
                  </div>
                </th>)}
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-224">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-225">
            {locations.map(location => <tr key={location.id} className="hover:bg-gray-50 transition-colors" data-id="element-226">
                <td className="px-6 py-4" data-id="element-227">
                  <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" checked={selectedLocations.includes(location.id)} onChange={e => onSelectionChange(e.target.checked ? [...selectedLocations, location.id] : selectedLocations.filter(id => id !== location.id))} data-id="element-228" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-229">
                  {location.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-230">
                  <div className="flex items-center" data-id="element-231">
                    <span className="text-sm font-medium text-primary-700" data-id="element-232">
                      {location.name}
                    </span>
                    {location.tags && location.tags.length > 0 && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-233">
                        {location.tags[0]}
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-234">
                  <span className="inline-flex items-center" data-id="element-235">
                    {getLocationTypeIcon(location.locationType)}
                    <span className="ml-1.5 text-sm text-gray-900" data-id="element-236">
                      {location.locationType}
                    </span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-237">
                  {location.addressLine1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-238">
                  {location.city}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-239">
                  {location.state}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-240">
                  {location.zip}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-241">
                  {new Date(location.lastUsedDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-242">
                  <div className="flex justify-end items-center space-x-3" data-id="element-243">
                    <button className="text-gray-400 hover:text-primary-700 transition-colors" title="View Details" data-id="element-244">
                      <EyeIcon size={18} data-id="element-245" />
                    </button>
                    <button className="text-gray-400 hover:text-primary-700 transition-colors" title="Edit" data-id="element-246">
                      <PencilIcon size={18} data-id="element-247" />
                    </button>
                    <button className="text-gray-400 hover:text-red-600 transition-colors" title="Delete" data-id="element-248">
                      <TrashIcon size={18} data-id="element-249" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {locations.length > 10 && <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50" data-id="element-250">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-251">
            <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-252">
              Previous
            </button>
            <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-253">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-254">
            <div data-id="element-255">
              <p className="text-sm text-gray-700" data-id="element-256">
                Showing <span className="font-medium" data-id="element-257">1</span> to{' '}
                <span className="font-medium" data-id="element-258">10</span> of{' '}
                <span className="font-medium" data-id="element-259">{locations.length}</span>{' '}
                locations
              </p>
            </div>
            <div data-id="element-260">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-261">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-262">
                  <span className="sr-only" data-id="element-263">Previous</span>
                  <ChevronUpIcon className="h-5 w-5" data-id="element-264" />
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-265">
                  <span className="sr-only" data-id="element-266">Next</span>
                  <ChevronDownIcon className="h-5 w-5" data-id="element-267" />
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
};
export default LocationList;